// AuthPage: Page component for login, registration and password-reset requests.
// Handles user authentication flows and triggers `onLoginSuccess` on success.
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { apiClient } from '../api/apiClient';
import { errorMessage } from '../api/errorMessage';
import './AuthPage.css';

export const PASSWORD_HINT = 'Mindestens 12 Zeichen — am einfachsten ein kurzer Satz oder mehrere Wörter.';

/**
 * AuthPage - Login, Registrierung und „Passwort vergessen"
 * Zeigt je nach aktiver Ansicht Login, Registrierung oder das Anfordern-Formular.
 * Die Anfordern-Ansicht ist per Router-State erreichbar (`state: { authView: 'forgot' }`),
 * damit die Reset-Seite bei ungültigem Link direkt dorthin verweisen kann.
 */
function AuthPage({ onLoginSuccess }) {
  const location = useLocation();
  const [view, setView] = useState(location.state?.authView === 'forgot' ? 'forgot' : 'login');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Login State
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Registrierung State
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerDisplayName, setRegisterDisplayName] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState('');

  // Passwort-vergessen State
  const [forgotEmail, setForgotEmail] = useState('');

  const switchView = (nextView) => {
    setView(nextView);
    setError(null);
    setSuccessMessage(null);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { data } = await apiClient.post('/api/auth/login', {
        email: loginEmail,
        password: loginPassword,
      });

      // Speichere Token und Benutzer-Daten
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setLoading(false);
      setSuccessMessage('Erfolgreich angemeldet!');

      // Rufe onLoginSuccess Callback auf
      setTimeout(() => {
        onLoginSuccess(data.user, data.token);
      }, 500);

    } catch (err) {
      setError(errorMessage(err, 'Anmeldung fehlgeschlagen. Bitte später erneut versuchen.'));
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    // Übereinstimmung sofort prüfen; die Passwortregeln selbst prüft nur das Backend
    // (eine Frontend-Kopie der Regeln driftet) und meldet Verstöße als Klartext-Fehler.
    if (registerPassword !== registerPasswordConfirm) {
      setError('Passwörter stimmen nicht überein.');
      setLoading(false);
      return;
    }

    try {
      const { data } = await apiClient.post('/api/auth/register', {
        email: registerEmail,
        displayName: registerDisplayName,
        password: registerPassword,
        passwordConfirm: registerPasswordConfirm,
      });

      // Speichere Token und Benutzer-Daten
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));

      setLoading(false);
      setSuccessMessage('Registrierung erfolgreich!');

      // Rufe onLoginSuccess Callback auf
      setTimeout(() => {
        onLoginSuccess(data.user, data.token);
      }, 500);

    } catch (err) {
      setError(errorMessage(err, 'Registrierung fehlgeschlagen.'));
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setLoading(true);

    try {
      const { data } = await apiClient.post('/api/auth/request-password-reset', { email: forgotEmail });

      setSuccessMessage(data.message);
      setLoading(false);
    } catch (err) {
      setError(errorMessage(err, 'Anfordern fehlgeschlagen.'));
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <h1>CoMana</h1>

        <div className="auth-tabs">
          <button
            className={`tab-button ${view === 'login' ? 'active' : ''}`}
            onClick={() => switchView('login')}
          >
            Anmelden
          </button>
          <button
            className={`tab-button ${view === 'register' ? 'active' : ''}`}
            onClick={() => switchView('register')}
          >
            Registrieren
          </button>
        </div>

        {/* Error Message */}
        {error && <div className="auth-error">{error}</div>}

        {/* Success Message */}
        {successMessage && <div className="auth-success">{successMessage}</div>}

        {/* Login Form */}
        {view === 'login' && (
          <form onSubmit={handleLogin} className="auth-form">
            <h2>Anmelden</h2>

            <div className="form-group">
              <label htmlFor="login-email">E-Mail:</label>
              <input
                id="login-email"
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="deine@email.de"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="login-password">Passwort:</label>
              <input
                id="login-password"
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••"
                required
              />
              <button
                type="button"
                className="auth-text-link"
                onClick={() => switchView('forgot')}
              >
                Passwort vergessen?
              </button>
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Wird angemeldet...' : 'Anmelden'}
            </button>
          </form>
        )}

        {/* Passwort-vergessen Form */}
        {view === 'forgot' && (
          <form onSubmit={handleForgotSubmit} className="auth-form">
            <h2>Passwort vergessen</h2>

            <p className="form-hint">
              Gib deine E-Mail-Adresse ein — wir schicken dir einen Link, mit dem du ein
              neues Passwort setzen kannst.
            </p>

            <div className="form-group">
              <label htmlFor="forgot-email">E-Mail:</label>
              <input
                id="forgot-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                placeholder="deine@email.de"
                required
              />
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Wird gesendet...' : 'Link anfordern'}
            </button>

            <button
              type="button"
              className="auth-text-link auth-text-link--center"
              onClick={() => switchView('login')}
            >
              Zurück zur Anmeldung
            </button>
          </form>
        )}

        {/* Register Form */}
        {view === 'register' && (
          <form onSubmit={handleRegister} className="auth-form">
            <h2>Registrieren</h2>

            <div className="form-group">
              <label htmlFor="register-email">E-Mail:</label>
              <input
                id="register-email"
                type="email"
                value={registerEmail}
                onChange={(e) => setRegisterEmail(e.target.value)}
                placeholder="deine@email.de"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="register-displayname">Anzeigename:</label>
              <input
                id="register-displayname"
                type="text"
                value={registerDisplayName}
                onChange={(e) => setRegisterDisplayName(e.target.value)}
                placeholder="Dein Anzeigename"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="register-password">Passwort:</label>
              <input
                id="register-password"
                type="password"
                value={registerPassword}
                onChange={(e) => setRegisterPassword(e.target.value)}
                placeholder="••••••"
                required
              />
              <p className="form-hint">{PASSWORD_HINT}</p>
            </div>

            <div className="form-group">
              <label htmlFor="register-password-confirm">Passwort bestätigen:</label>
              <input
                id="register-password-confirm"
                type="password"
                value={registerPasswordConfirm}
                onChange={(e) => setRegisterPasswordConfirm(e.target.value)}
                placeholder="••••••"
                required
              />
            </div>

            <button
              type="submit"
              className="auth-button"
              disabled={loading}
            >
              {loading ? 'Wird registriert...' : 'Registrieren'}
            </button>
          </form>
        )}

        <div className="auth-footer">
          <Link to="/impressum">Impressum</Link>
          <span className="auth-footer__sep">·</span>
          <Link to="/datenschutz">Datenschutz</Link>
          <span className="auth-footer__sep">·</span>
          <Link to="/kontakt">Kontakt</Link>
        </div>
      </div>

      <div className="auth-disclaimer">
        <p>
          CoMana is unofficial Fan Content permitted under the{' '}
          <a href="https://company.wizards.com/en/legal/fancontentpolicy" target="_blank" rel="noopener noreferrer">Fan Content Policy</a>.
          Not approved/endorsed by Wizards. Portions of the materials used are property of Wizards of the Coast.
          ©Wizards of the Coast LLC.
        </p>
        <p>
          Card data and images provided by{' '}
          <a href="https://scryfall.com" target="_blank" rel="noopener noreferrer">Scryfall</a>.
          Not affiliated with Scryfall.
        </p>
      </div>
    </div>
  );
}

export default AuthPage;
